import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getMenuByRoleId } from '../Services/RolesPermissions/MenuManagemnet/GetMenusPermissions';
import {
    PERMISSION_STATE,
    normalizePath,
    buildMenuTreeAndPathMap,
    findMenuItemForPath,
    normalizePermissions
} from '../utils.js/permissionUtils';

const PermissionContext = createContext();

const DEFAULT_PERMISSIONS = Object.freeze({
    create: false,
    read: false,
    update: false,
    delete: false,
    view: false
});

export { PERMISSION_STATE };

export const PermissionProvider = ({ children }) => {
    // Dynamic menus fetched from get-menu-by-role-id
    const [dynamicMenus, setDynamicMenus] = useState([]);
    const [loadingMenus, setLoadingMenus] = useState(false);
    const [menusLoaded, setMenusLoaded] = useState(false);

    // Cache of resolved route permissions: { [normalizedPath]: { status, permissions, deniedMessage, statusBadge } }
    const [routePermissionsCache, setRoutePermissionsCache] = useState({});

    // Current active route and permissions (for PermissionWrapper and components using usePermissions)
    const [activePath, setActivePath] = useState(null);
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [accessDenied, setAccessDenied] = useState(false);
    const [accessDeniedMessage, setAccessDeniedMessage] = useState(null);
    const [routeLoading, setRouteLoading] = useState(false);

    // References to prevent duplicate concurrent API calls and break infinite dependency loops
    const inFlightMenusPromise = useRef(null);
    const inFlightRoutePromises = useRef({});
    const dynamicMenusRef = useRef(dynamicMenus);
    const menusLoadedRef = useRef(menusLoaded);
    const routePermissionsCacheRef = useRef(routePermissionsCache);

    useEffect(() => {
        dynamicMenusRef.current = dynamicMenus;
    }, [dynamicMenus]);

    useEffect(() => {
        menusLoadedRef.current = menusLoaded;
    }, [menusLoaded]);

    useEffect(() => {
        routePermissionsCacheRef.current = routePermissionsCache;
    }, [routePermissionsCache]);

    // Build navigation tree and path map from dynamicMenus
    const { tree: menuTree, pathMap } = useMemo(() => {
        return buildMenuTreeAndPathMap(dynamicMenus);
    }, [dynamicMenus]);

    // Fetch dynamic menus for the role (single source of truth, stable identity)
    const fetchMenus = useCallback(async (force = false) => {
        // If already loaded or attempted, and not forced, return cached data
        if (!force && menusLoadedRef.current) {
            return dynamicMenusRef.current;
        }

        if (inFlightMenusPromise.current) {
            return inFlightMenusPromise.current;
        }

        const promise = (async () => {
            try {
                setLoadingMenus(true);
                const response = await getMenuByRoleId();
                const data = response?.data || [];
                setDynamicMenus(data);
                dynamicMenusRef.current = data;
                setMenusLoaded(true);
                menusLoadedRef.current = true;

                // Auto-sync roleId if not already in localStorage
                let storedRoleId = localStorage.getItem('roleId');
                if (!storedRoleId && data.length > 0) {
                    const firstGroup = data[0];
                    const firstItem = Array.isArray(firstGroup.menuItems) ? firstGroup.menuItems[0] : firstGroup;
                    const rId = firstItem?.roleId || firstItem?.role_id || firstGroup?.roleId;
                    if (rId) {
                        localStorage.setItem('roleId', rId);
                    }
                }
                return data;
            } catch (err) {
                console.error("PermissionContext: Failed to fetch menus by role:", err);
                setDynamicMenus([]);
                dynamicMenusRef.current = [];
                setMenusLoaded(true);
                menusLoadedRef.current = true;
                return [];
            } finally {
                setLoadingMenus(false);
                inFlightMenusPromise.current = null;
            }
        })();

        inFlightMenusPromise.current = promise;
        return promise;
    }, []);

    // Synchronously get cached permission state for a route
    const getRoutePermission = useCallback((pathname) => {
        const normalized = normalizePath(pathname);
        if (routePermissionsCacheRef.current[normalized]) {
            return routePermissionsCacheRef.current[normalized];
        }

        return {
            status: PERMISSION_STATE.UNKNOWN,
            permissions: DEFAULT_PERMISSIONS,
            deniedMessage: null,
            statusBadge: null
        };
    }, []);

    // Centralized route permission resolver (stable identity)
    const resolvePermissionForRoute = useCallback(async (pathname) => {
        const normalized = normalizePath(pathname);

        // 1. Return immediately if already known in cache
        const cached = routePermissionsCacheRef.current[normalized];
        if (cached && (cached.status === PERMISSION_STATE.ALLOWED || cached.status === PERMISSION_STATE.DENIED)) {
            setActivePath(normalized);
            setPermissions(cached.permissions);
            setAccessDenied(cached.status === PERMISSION_STATE.DENIED);
            setAccessDeniedMessage(cached.deniedMessage);
            return cached;
        }

        // 2. Return in-flight promise if already resolving this route
        if (inFlightRoutePromises.current[normalized]) {
            return inFlightRoutePromises.current[normalized];
        }

        // Set route state to LOADING
        setRouteLoading(true);
        setRoutePermissionsCache(prev => {
            const next = {
                ...prev,
                [normalized]: {
                    status: PERMISSION_STATE.LOADING,
                    permissions: DEFAULT_PERMISSIONS,
                    deniedMessage: null,
                    statusBadge: null
                }
            };
            routePermissionsCacheRef.current = next;
            return next;
        });

        const resolverPromise = (async () => {
            try {
                // Ensure menus are loaded
                let currentMenus = dynamicMenusRef.current;
                if (!menusLoadedRef.current) {
                    currentMenus = await fetchMenus();
                }

                // Get current pathMap
                const { pathMap: currentPathMap } = buildMenuTreeAndPathMap(currentMenus);

                // Special handling for dashboard & user profile
                if (normalized === '/' || normalized === '/dashboard') {
                    const dashboardItem = findMenuItemForPath(currentPathMap, '/dashboard');
                    if (!dashboardItem || dashboardItem.view !== false) {
                        const allowedResult = {
                            status: PERMISSION_STATE.ALLOWED,
                            permissions: { create: true, read: true, update: true, delete: true, view: true },
                            deniedMessage: null,
                            statusBadge: null
                        };
                        setRoutePermissionsCache(prev => {
                            const next = { ...prev, [normalized]: allowedResult };
                            routePermissionsCacheRef.current = next;
                            return next;
                        });
                        setActivePath(normalized);
                        setPermissions(allowedResult.permissions);
                        setAccessDenied(false);
                        setAccessDeniedMessage(null);
                        return allowedResult;
                    }
                }

                if (normalized === '/profile') {
                    const allowedResult = {
                        status: PERMISSION_STATE.ALLOWED,
                        permissions: { create: true, read: true, update: true, delete: false, view: true },
                        deniedMessage: null,
                        statusBadge: null
                    };
                    setRoutePermissionsCache(prev => {
                        const next = { ...prev, [normalized]: allowedResult };
                        routePermissionsCacheRef.current = next;
                        return next;
                    });
                    setActivePath(normalized);
                    setPermissions(allowedResult.permissions);
                    setAccessDenied(false);
                    setAccessDeniedMessage(null);
                    return allowedResult;
                }

                // Match menu for current path
                const matchedMenu = findMenuItemForPath(currentPathMap, normalized);

                // Special handling for FAQ routes: /content-hub/faqs should inherit from /content-hub/faqs-types
                if (!matchedMenu && (normalized === '/content-hub/faqs' || normalized === '/content-hub/faqs-types')) {
                    const fallbackPath = normalized === '/content-hub/faqs' ? '/content-hub/faqs-types' : '/content-hub/faqs';
                    const fallbackMenu = findMenuItemForPath(currentPathMap, fallbackPath);
                    if (fallbackMenu) {
                        const allowedResult = {
                            status: PERMISSION_STATE.ALLOWED,
                            permissions: normalizePermissions(fallbackMenu),
                            deniedMessage: null,
                            statusBadge: null
                        };
                        setRoutePermissionsCache(prev => {
                            const next = { ...prev, [normalized]: allowedResult };
                            routePermissionsCacheRef.current = next;
                            return next;
                        });
                        setActivePath(normalized);
                        setPermissions(allowedResult.permissions);
                        setAccessDenied(false);
                        setAccessDeniedMessage(null);
                        return allowedResult;
                    }
                }

                // If no menu definition exists for this role -> Access Denied
                if (!matchedMenu) {
                    const deniedResult = {
                        status: PERMISSION_STATE.DENIED,
                        permissions: DEFAULT_PERMISSIONS,
                        deniedMessage: "Access denied: You do not have permission to view in menus",
                        statusBadge: "403 Forbidden"
                    };
                    setRoutePermissionsCache(prev => {
                        const next = { ...prev, [normalized]: deniedResult };
                        routePermissionsCacheRef.current = next;
                        return next;
                    });
                    setActivePath(normalized);
                    setPermissions(DEFAULT_PERMISSIONS);
                    setAccessDenied(true);
                    setAccessDeniedMessage(deniedResult.deniedMessage);
                    return deniedResult;
                }

                // If role's menu has view explicitly false -> Access Denied
                if (matchedMenu.view === false) {
                    const deniedResult = {
                        status: PERMISSION_STATE.DENIED,
                        permissions: DEFAULT_PERMISSIONS,
                        deniedMessage: "Access denied: You do not have permission to view in menus",
                        statusBadge: "403 Forbidden"
                    };
                    setRoutePermissionsCache(prev => {
                        const next = { ...prev, [normalized]: deniedResult };
                        routePermissionsCacheRef.current = next;
                        return next;
                    });
                    setActivePath(normalized);
                    setPermissions(DEFAULT_PERMISSIONS);
                    setAccessDenied(true);
                    setAccessDeniedMessage(deniedResult.deniedMessage);
                    return deniedResult;
                }

                // Menu exists and has view: true in role's menu assignments -> ACCESS ALLOWED!
                const normalizedPerms = normalizePermissions(matchedMenu);
                const allowedResult = {
                    status: PERMISSION_STATE.ALLOWED,
                    permissions: normalizedPerms,
                    deniedMessage: null,
                    statusBadge: null
                };
                setRoutePermissionsCache(prev => {
                    const next = { ...prev, [normalized]: allowedResult };
                    routePermissionsCacheRef.current = next;
                    return next;
                });
                setActivePath(normalized);
                setPermissions(normalizedPerms);
                setAccessDenied(false);
                setAccessDeniedMessage(null);
                return allowedResult;
            } finally {
                setRouteLoading(false);
                delete inFlightRoutePromises.current[normalized];
            }
        })();

        inFlightRoutePromises.current[normalized] = resolverPromise;
        return resolverPromise;
    }, [fetchMenus]);

    // Invalidate caches when permissions or roles change
    const invalidatePermissions = useCallback(() => {
        setRoutePermissionsCache({});
        routePermissionsCacheRef.current = {};
        setDynamicMenus([]);
        dynamicMenusRef.current = [];
        setMenusLoaded(false);
        menusLoadedRef.current = false;
        inFlightMenusPromise.current = null;
        inFlightRoutePromises.current = {};
    }, []);

    // Clear cache completely (on logout)
    const clearPermissionCache = useCallback(() => {
        setRoutePermissionsCache({});
        setPermissions(DEFAULT_PERMISSIONS);
        setAccessDenied(false);
        setAccessDeniedMessage(null);
        setActivePath(null);
        setDynamicMenus([]);
        setMenusLoaded(false);
        inFlightMenusPromise.current = null;
        inFlightRoutePromises.current = {};
    }, []);

    // Automatically clear cache if user is logged out
    useEffect(() => {
        const checkAuth = () => {
            const isAuth = localStorage.getItem('isAuthenticated') === 'true';
            if (!isAuth) {
                clearPermissionCache();
            }
        };
        checkAuth();
        window.addEventListener('storage', checkAuth);
        return () => window.removeEventListener('storage', checkAuth);
    }, [clearPermissionCache]);

    // Backward-compatible updatePermissions
    const updatePermissions = useCallback((newPerms) => {
        if (!newPerms) {
            setPermissions(DEFAULT_PERMISSIONS);
            return;
        }
        let data = newPerms.data !== undefined ? newPerms.data : newPerms;
        if (Array.isArray(data)) {
            data = data.length > 0 ? data[0] : DEFAULT_PERMISSIONS;
        }
        setPermissions(normalizePermissions(data));
    }, []);

    const setAccessDeniedState = useCallback((denied, message = null) => {
        setAccessDenied(denied);
        setAccessDeniedMessage(message);
    }, []);

    const clearAccessDenied = useCallback(() => {
        setAccessDenied(false);
        setAccessDeniedMessage(null);
    }, []);

    const startLoading = useCallback(() => {
        setRouteLoading(true);
    }, []);

    return (
        <PermissionContext.Provider value={{
            // States
            permissions,
            loading: routeLoading || loadingMenus,
            accessDenied,
            accessDeniedMessage,
            activePath,

            // New permission-first architecture
            PERMISSION_STATE,
            dynamicMenus,
            menuTree,
            pathMap,
            loadingMenus,
            fetchMenus,
            getRoutePermission,
            resolvePermissionForRoute,
            invalidatePermissions,
            clearPermissionCache,

            // Backward compatibility
            updatePermissions,
            setAccessDeniedState,
            clearAccessDenied,
            startLoading
        }}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (context === undefined) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
};
